import { Controller } from "@hotwired/stimulus"
import { formatValue } from "../lib/number_format"
import { track } from "../lib/umami"
import {
  americanToDecimal,
  decimalToAmerican,
  decimalToFractional,
  decimalToProbability,
  fractionalToDecimal,
  oddsWarning,
  parseDecimalOdds,
  parsePercentage,
  probabilityToDecimal,
  roundTo,
  stakeReturn
} from "../lib/odds_math"

// Convertisseur de cotes autonome (/convertisseur-cotes, #1836).
//
// Les quatre champs de format décrivent le même prix : saisir dans l'un met les
// trois autres à jour. Le champ en cours de frappe n'est jamais réécrit, sans quoi
// le curseur sauterait à chaque caractère.
//
// Sans JavaScript, la page reste complète : les champs affichent l'exemple par
// défaut et tout le contenu pédagogique est servi par le serveur.
export default class extends Controller {
  static targets = [ "field", "warning", "warningText", "copyButton", "results" ]
  static values = {
    locale: { type: String, default: "fr" },
    messages: { type: Object, default: {} }
  }

  connect() {
    this.defaults = new Map(this.fieldTargets.map((field) => [ field.dataset.format, field.value ]))
    this.copyLabel = this.hasCopyButtonTarget ? this.copyButtonTarget.textContent : null
    this.applyQueryParams()
    this.convert()
  }

  // Une URL partageable : ?decimal=2%2C50&stake=10 rejoue la même saisie.
  applyQueryParams() {
    const params = new URLSearchParams(window.location.search)
    const decimal = params.get("decimal")
    const stake = params.get("stake")
    if (decimal) this.fieldFor("decimal").value = decimal
    if (stake) this.fieldFor("stake").value = stake
  }

  convert(event) {
    this.trackUse(event)

    const source = event?.target?.dataset?.format || "decimal"
    const decimal = source === "stake" ? this.currentDecimal() : this.decimalFrom(source)

    this.renderWarning(this.warningFor(source, decimal))
    this.syncFields(decimal, source)
    this.renderResults(decimal)
    this.updateShareableUrl(decimal)
  }

  applyPreset(event) {
    track("oddsConverterPreset_applied")
    this.fieldFor("decimal").value = event.currentTarget.dataset.decimal
    this.convert()
  }

  reset() {
    track("oddsConverter_reset")
    this.fieldTargets.forEach((field) => {
      const initial = this.defaults?.get(field.dataset.format)
      if (initial !== undefined) field.value = initial
    })
    this.convert()
  }

  async copy() {
    if (!this.hasCopyButtonTarget) return

    try {
      await navigator.clipboard.writeText(this.summary())
      track("oddsConverterResult_copied")
      this.copyButtonTarget.textContent = this.messagesValue.copied || this.copyLabel
      window.setTimeout(() => { this.copyButtonTarget.textContent = this.copyLabel }, 2000)
    } catch {
      // Presse-papiers refusé (permission, contexte non sécurisé) : on laisse le
      // libellé inchangé plutôt que d'afficher une confirmation mensongère.
    }
  }

  // Le formulaire pointe vers sa propre URL en GET : sans JS, une soumission
  // recharge simplement la page avec les paramètres. Avec JS, on garde l'URL à
  // jour sans navigation.
  preventSubmit(event) {
    event.preventDefault()
    this.convert()
  }

  // -------------------------------------------------------------------------

  // Une seule fois par visite, au premier changement saisi par l'utilisateur : le
  // convert() du connect() ne doit pas compter comme un usage.
  trackUse(event) {
    if (this.used || event?.type !== "input") return

    this.used = true
    track("oddsConverter_used")
  }

  fieldFor(format) {
    return this.fieldTargets.find((field) => field.dataset.format === format)
  }

  valueFor(format) {
    return this.fieldFor(format)?.value
  }

  currentDecimal() {
    return parseDecimalOdds(this.valueFor("decimal"))
  }

  // Cote décimale déduite du champ effectivement modifié : c'est lui qui fait foi.
  decimalFrom(source) {
    switch (source) {
      case "fractional": return fractionalToDecimal(this.valueFor("fractional"))
      case "american": return americanToDecimal(this.valueFor("american"))
      case "probability": return probabilityToDecimal(parsePercentage(this.valueFor("probability")))
      default: return parseDecimalOdds(this.valueFor("decimal"))
    }
  }

  syncFields(decimal, source) {
    if (decimal === null || decimal <= 1) return

    const american = decimalToAmerican(decimal)
    const values = {
      decimal: formatValue(decimal, "odds", this.localeValue),
      fractional: decimalToFractional(decimal)?.label ?? "",
      american: american === null ? "" : formatValue(american, "american", this.localeValue),
      probability: formatValue(decimalToProbability(decimal) * 100, "odds", this.localeValue)
    }

    Object.entries(values).forEach(([ format, value ]) => {
      if (format === source) return

      const field = this.fieldFor(format)
      if (field && value !== "") field.value = value
    })
  }

  renderResults(decimal) {
    const payout = decimal === null ? null : stakeReturn({ odds: decimal, stake: this.valueFor("stake") })
    const values = { gross_return: payout?.gross ?? null, net_profit: payout?.net ?? null }

    this.element.querySelectorAll("[data-output]").forEach((output) => {
      const value = values[output.dataset.output]
      output.textContent = value === null || value === undefined
        ? "—"
        : formatValue(value, output.dataset.format, this.localeValue)
    })
  }

  warningFor(source, decimal) {
    if (decimal === null) return "invalid_number"
    if (source === "probability") {
      const probability = parsePercentage(this.valueFor("probability"))
      if (probability === null || probability <= 0 || probability > 1) return "probability_out_of_range"
    }
    return oddsWarning(decimal)
  }

  renderWarning(code) {
    if (!this.hasWarningTarget) return

    if (!code) {
      this.warningTarget.hidden = true
      if (this.hasWarningTextTarget) this.warningTextTarget.textContent = ""
      return
    }

    this.warningTarget.hidden = false
    if (this.hasWarningTextTarget) this.warningTextTarget.textContent = this.messagesValue[code] || ""
  }

  updateShareableUrl(decimal) {
    if (decimal === null) return

    const params = new URLSearchParams(window.location.search)
    params.set("decimal", formatValue(roundTo(decimal, 2), "odds", this.localeValue))
    params.set("stake", this.valueFor("stake") || "")
    window.history.replaceState({}, "", `${window.location.pathname}?${params}`)
  }

  summary() {
    const formats = [ "decimal", "fractional", "american", "probability" ]
      .map((format) => `${this.labelFor(format)}: ${this.valueFor(format)}`)
    const results = Array.from(this.element.querySelectorAll("[data-output]"))
      .map((output) => `${output.closest(".calc-result").querySelector("dt").textContent}: ${output.textContent}`)

    return [ ...formats, ...results ].join("\n")
  }

  labelFor(format) {
    const field = this.fieldFor(format)
    return field ? this.element.querySelector(`label[for="${field.id}"]`)?.textContent.trim() || format : format
  }
}
